import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Avatar, IconButton, InputBase, Button, Drawer, Divider, CircularProgress } from '@mui/material';
import { MoreVert as MoreVertIcon, Send as SendIcon, Group as GroupIcon, WifiOff as WifiOffIcon } from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  loadMoreMessages,
  sendMessage,
  retryMessage,
} from '../redux/chat.redux';
import { api } from '../api/chat';
import ChatMessage from './chat-message';
import { PrivateSettingsPanel, GroupSettingsPanel } from './settings-panels';
import ForwardDialog from './dialogs/forward-dialog';
import type { Message } from '../types/message';
import { avatarText } from '../utils/format';

export default function ChatContainer() {
  const dispatch = useAppDispatch();
  const activeId = useAppSelector((s) => s.chat.activeConversationId);
  const conversations = useAppSelector((s) => s.chat.conversations);
  const messages = useAppSelector((s) => (activeId ? s.chat.messages[activeId] : undefined));
  const hasMore = useAppSelector((s) => (activeId ? s.chat.hasMore[activeId] : false));
  const me = useAppSelector((s) => s.user.user);
  const blocked = useAppSelector((s) => s.chat.blocked);
  const wsConnected = useAppSelector((s) => s.chat.wsConnected);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [text, setText] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [forwardMsg, setForwardMsg] = useState<Message | null>(null);
  const listRef = useRef<HTMLElement | null>(null);

  const conversation = conversations.find((c) => c.id === activeId);

  // 切换会话 → 滚动到底部
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [activeId]);

  // 新消息 → 滚动到底部(仅当接近底部)
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [messages?.length]);

  if (!conversation || !activeId) {
    return (
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f1f5', color: 'text.secondary' }}>
        <GroupIcon sx={{ fontSize: 64, color: 'rgba(39,42,80,0.15)' }} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          选择会话开始聊天
        </Typography>
      </Box>
    );
  }

  const isGroup = conversation.type === 'group';
  const peerBlocked = !isGroup && blocked.some((b) => b.id === conversation.peer_id);
  const peer = isGroup
    ? null
    : {
        id: conversation.peer_id,
        nickname: conversation.display_name,
        avatar: conversation.display_avatar,
        username: conversation.display_name,
      };

  const onScroll = async (e: React.UIEvent) => {
    const el = e.currentTarget;
    if (el.scrollTop < 40 && hasMore && !loadingMore) {
      setLoadingMore(true);
      try {
        await dispatch(loadMoreMessages(activeId)).unwrap();
      } catch {
        /* ignore */
      } finally {
        setLoadingMore(false);
      }
    }
  };

  const doSend = () => {
    const content = text.trim();
    if (!content) return;
    setText('');
    dispatch(sendMessage({ cid: activeId, content }));
  };

  const doRecall = async (m: Message) => {
    try {
      await api.recallMessage(m.id);
    } catch (err: any) {
      alert(err?.message || '撤回失败');
    }
  };

  const doDelete = async (m: Message) => {
    if (!window.confirm('删除该消息?')) return;
    try {
      await api.deleteMessage(m.id);
    } catch (err: any) {
      alert(err?.message || '删除失败');
    }
  };

  const doRetry = (m: Message) => {
    if (m.client_id) {
      dispatch(retryMessage({ cid: m.conversation_id, clientId: m.client_id, content: m.content }));
    }
  };

  const onSettingsClose = () => {
    setSettingsOpen(false);
  };

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0, bgcolor: '#f0f1f5' }}>
      {/* 标题栏 */}
      <Box
        sx={{
          height: 56,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0,0,0,0.1)',
          bgcolor: '#fff',
        }}
      >
        <Avatar src={conversation.display_avatar || undefined} sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 15 }}>
          {avatarText({ nickname: conversation.display_name, username: conversation.display_name })}
        </Avatar>
        <Box sx={{ ml: 1.5, minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 600, fontSize: 15, lineHeight: 1.2 }}>
            {conversation.display_name}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isGroup ? (
              '群聊'
            ) : (
              <>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: grey[400] }} />
                {peerBlocked ? '已拉黑' : '离线'}
              </>
            )}
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        {!wsConnected && (
          <Typography variant="caption" sx={{ color: 'warning.main', mr: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <WifiOffIcon fontSize="small" /> 连接中
          </Typography>
        )}
        <IconButton onClick={() => setSettingsOpen(true)}>
          <MoreVertIcon />
        </IconButton>
      </Box>

      {/* 消息列表 */}
      <Box ref={listRef} onScroll={onScroll} sx={{ flexGrow: 1, overflowY: 'auto', py: 2 }}>
        {loadingMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {!messages || messages.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 6 }}>
            暂无消息,发一条问候吧
          </Typography>
        ) : (
          messages.map((m) => (
            <ChatMessage
              key={m.client_id ? `${m.id}-${m.client_id}` : m.id}
              message={m}
              meId={me?.id || 0}
              group={isGroup}
              onRecall={m.status !== 'failed' ? doRecall : undefined}
              onDelete={m.status !== 'failed' ? doDelete : undefined}
              onForward={m.status !== 'failed' ? (msg) => setForwardMsg(msg) : undefined}
              onRetry={m.status === 'failed' ? doRetry : undefined}
            />
          ))
        )}
      </Box>

      {/* 输入区 */}
      <Box sx={{ bgcolor: '#fff', borderTop: '1px solid rgba(0,0,0,0.1)', p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, bgcolor: '#f5f6f8', borderRadius: 2, px: 1.5, py: 0.5 }}>
          <InputBase
            fullWidth
            multiline
            maxRows={4}
            placeholder="输入消息,Enter 发送"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                doSend();
              }
            }}
            sx={{ fontSize: 14 }}
          />
          <Button variant="contained" disabled={!text.trim()} onClick={doSend} sx={{ borderRadius: 2, minWidth: 72, flexShrink: 0 }}>
            <SendIcon fontSize="small" />
          </Button>
        </Box>
      </Box>

      {/* 右侧设置面板 */}
      <Drawer anchor="right" open={settingsOpen} onClose={onSettingsClose} PaperProps={{ sx: { width: 320 } }}>
        <Box
          sx={{
            height: 56,
            display: 'flex',
            alignItems: 'center',
            px: 2,
            borderBottom: '1px solid rgba(0,0,0,0.1)',
            bgcolor: 'primary.main',
            color: '#fff',
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>{isGroup ? '群设置' : '聊天设置'}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton onClick={onSettingsClose} sx={{ color: '#fff' }}>
            <MoreVertIcon sx={{ transform: 'rotate(90deg)' }} />
          </IconButton>
        </Box>
        <Divider />
        {isGroup ? (
          <GroupSettingsPanel conversation={conversation} onClose={onSettingsClose} />
        ) : (
          <PrivateSettingsPanel conversation={conversation} peer={peer} peerBlocked={peerBlocked} onClose={onSettingsClose} />
        )}
      </Drawer>

      <ForwardDialog open={Boolean(forwardMsg)} message={forwardMsg} onClose={() => setForwardMsg(null)} />
    </Box>
  );
}
